import React, {useRef, useState} from 'react';
import {FormGroup, Label} from 'reactstrap';
import {Form} from '@unform/web';
import {Input as UInput} from '../../components/input';
import Yup from '../../infra/Yup';
import ReactSelect from '../../components/react-select';
import './notification.sass';
import ToggleButton from '../../components/toggle-button';
import I18nMessage from '../../components/I18nMessage';
import {FieldSet} from '../../components/fieldset';
import {GroupButtonForm} from '../../components/group-button-form';

const NotificationForm = (props) => {
    const {
        cancel,
        data,
        edit,
        newRegister,
        updateRegister,
        alarmTypes,
        allNotificationTypes,
        allNotificator,
        calendars,
        geofences,
        i18n,
        loading,
    } = props;

    const formRef = useRef(null);

    const isGeofenceType = (type) => type === 'geofenceEnter' || type === 'geofenceExit';

    const [showFieldAlarms, showAlarms] = useState(edit ? data.type === 'alarm' : false);
    const [showFieldGeofence, showGeofence] = useState(edit ? isGeofenceType(data.type) : false);
    const [selectedType, setSelectedType] = useState(edit ? data.type : null);
    const [alwaysOn, setAlwaysOn] = useState(edit ? !!data.always : false);

    const schema = Yup.object().shape({
        type: Yup.string().required(),
        // reportReady só acha destinatário via CacheManager.getDeviceNotifications quando
        // "sempre" está marcado (ver TaskReportJobs.notifyReportReady no backend) -- sem isso a
        // notificação existe mas nunca dispara, um jeito silencioso de nunca funcionar.
        always: Yup.mixed().when('type', {
            is: (type) => type === 'reportReady',
            then: (fieldSchema) => fieldSchema.test(
                'report-ready-requires-always',
                'report-ready-requires-always',
                (value) => value === true,
            ),
            otherwise: (fieldSchema) => fieldSchema.notRequired(),
        }),
        alarms: Yup.array().when('type', {
            is: 'alarm',
            then: (alarms) => alarms.min(1).required(),
            otherwise: (alarms) => alarms.notRequired(),
        }),
        geofences: Yup.array().when('type', {
            is: (type) => isGeofenceType(type),
            then: (geofences) => geofences.min(1).required(),
            otherwise: (geofences) => geofences.notRequired(),
        }),
        notificators: Yup.array().min(1).required(),
        id: Yup.number().transform((value) => (isNaN(value) ? undefined : parseInt(value))),
    });

    const handleSubmit = async (data) => {
        try {
            await schema.validate(data, {
                abortEarly: false,
            });

            data = schema.cast(data);

            data.notificators = data.notificators.toString();

            if (data.type === 'alarm') {
                data.attributes = {alarms: data.alarms.toString()};
                delete data.alarms;
            }

            if (isGeofenceType(data.type)) {
                data.attributes = {geofences: data.geofences.toString()};
                delete data.geofences;
            }

            edit ? updateRegister(data) : newRegister(data);
        } catch (err) {
            const validationErrors = {};
            if (err instanceof Yup.ValidationError) {
                err.inner.forEach((error) => {
                    validationErrors[error.path] = error.message;
                });
                formRef.current.setErrors(validationErrors);
            }
        }
    };

    const onChange = (selectedOption) => {
        if (selectedOption !== null) {
            showAlarms(selectedOption.id === 'alarm');
            showGeofence(isGeofenceType(selectedOption.id));
            setSelectedType(selectedOption.id);
        } else {
            showAlarms(edit ? data.type === 'alarm' : false);
            showGeofence(edit ? isGeofenceType(data.type) : false);
            setSelectedType(edit ? data.type : null);
        }
    };

    const renderAlarm = () => {
        if (showFieldAlarms) {
            return (
                <FormGroup className="custom-formGroup">
                    <Label for="name">{i18n.sharedAlarms}</Label>
                    <ReactSelect id={'alarms'} name="alarms" options={alarmTypes} multiple={true}/>
                </FormGroup>);
        }
        return null;
    };

    const renderGeofence = () => {
        if (showFieldGeofence) {
            return (
                <FormGroup className="custom-formGroup">
                    <Label for="geofences"><I18nMessage id="sharedGeofence"/></Label>
                    <ReactSelect id={'geofences'} name="geofences" options={geofences} multiple={true}/>
                </FormGroup>);
        }
        return null;
    };

    return (
        <Form ref={formRef} onSubmit={handleSubmit} schema={schema} initialData={data}>
            {!!edit && <UInput hidden type="number" name="id"/>}

            <FieldSet className="text-left border p-4" title={<I18nMessage id="sharedIdentification"/>}>
               <>
                   <FormGroup className="custom-formGroup">
                       <Label for="name"><I18nMessage id="notificationType"/>*</Label>
                       <ReactSelect id={'type'} name="type"
                                    onChange={onChange}
                                    options={allNotificationTypes}/>
                   </FormGroup>
                   <FormGroup className="custom-formGroup">
                       <Label><I18nMessage id="notificationAlways"/></Label>
                       <ToggleButton name={'always'} id={'always'} onValueChange={setAlwaysOn}/>
                   </FormGroup>
                   {/* O ToggleButton não mostra erro de campo: este aviso é a mensagem de validação de "Sempre",
                       por isso some quando ele é ligado e o Salvar volta a funcionar. */}
                   {selectedType === 'reportReady' && !alwaysOn && (
                       <p className="text-danger">
                           <I18nMessage id="notificationReportReadyRequiresAlways"/>
                       </p>
                   )}
                   {renderAlarm()}
                   {renderGeofence()}
                   <FormGroup className="custom-formGroup">
                       <Label for="name"><I18nMessage id="notificationNotificators"/>*</Label>
                       <ReactSelect id={'notificators'} name="notificators" options={allNotificator}
                                    multiple={true}/>
                   </FormGroup>
                   <FormGroup className="custom-formGroup">
                       <Label for="groupId"><I18nMessage id="sharedCalendar"/></Label>
                       <ReactSelect id={'calendarId'} name="calendarId" options={calendars}/>
                   </FormGroup>
               </>
            </FieldSet>
            <br/>
            <GroupButtonForm
                loading={loading}
                cancel={cancel}
                submit={() => formRef.current.submitForm()}
            />
        </Form>
    );
};

export default NotificationForm;
